// @ts-nocheck
import { FORTA_BLACK, FORTA_BLUE, FORTA_ERROR, FORTA_GRAY1, FORTA_GRAY5 } from '../../../../utils/colors';
import { INPUT_LABEL_NUDGE } from '../constants';

import type { SxProps, Theme } from '@mui/material';

type OutlinedState = { error: boolean; disabled: boolean | undefined };

export type FRadioGroupAriaIds = {
  labelId: string;
  helperTextId: string;
};

type BuildRadioGroupLayoutSxParams = {
  row: boolean;
  radioGroupSx: SxProps<Theme> | undefined;
  formControlLabelRowSx: SxProps<Theme>;
  formControlLabelVerticalSx: SxProps<Theme>;
};

function resolveRadioGroupBaseId(idProp: string | undefined, generatedId: string): string {
  return idProp != null && idProp !== '' ? idProp : generatedId;
}

export function buildRadioGroupAriaIds(
  idProp: string | undefined,
  generatedId: string,
): FRadioGroupAriaIds {
  const baseId = resolveRadioGroupBaseId(idProp, generatedId);
  return {
    labelId: `fradio-group-label-${baseId}`,
    helperTextId: `fradio-group-helper-${baseId}`,
  };
}

export function buildRadioGroupLayoutSx({
  row,
  radioGroupSx,
  formControlLabelRowSx,
  formControlLabelVerticalSx,
}: BuildRadioGroupLayoutSxParams): SxProps<Theme> {
  const base: SxProps<Theme> = (theme) => {
    /**
     * Match MUI small `OutlinedInput` insets: 8.5px vertical x 14px horizontal (same as `FInput` with `size="small"`).
     */
    return {
      width: '100%',
      minWidth: 0,
      minHeight: 0,
      margin: 0,
      boxSizing: 'border-box',
      columnGap: row ? theme.spacing(0.75) : 0,
      rowGap: row ? 0 : theme.spacing(0.25),
      '& .MuiFormControlLabel-label': { marginLeft: 0, marginRight: 0, paddingLeft: 0 },
      '& .MuiFormControlLabel-root': row ? formControlLabelRowSx : formControlLabelVerticalSx,
    };
  };

  if (radioGroupSx == null) {
    return base;
  }

  return [base, radioGroupSx];
}

export function getRadioGroupOutlinedBoxSx(state: OutlinedState): SxProps<Theme> {
  return (theme) => {
    const border = state.error ? FORTA_ERROR : FORTA_BLACK;
    return {
      py: 0.5,
      pr: 0.5,
      position: 'relative',
      boxSizing: 'border-box',
      border: `1px solid ${border}`,
      borderRadius: 1.5,
      transition: theme.transitions.create('border-color', {
        duration: theme.transitions.duration.shorter,
      }),
      '&:hover': {
        border: `1px solid ${border}`,
      },
      ...((state.disabled ?? false)
        ? {
            pointerEvents: 'none' as const,
            backgroundColor: FORTA_GRAY1,
            '& .MuiFormControlLabel-label.Mui-disabled': { color: FORTA_BLACK },
            '& .MuiRadio-root.Mui-disabled': { color: FORTA_GRAY5 },
            '& .MuiRadio-root.Mui-checked.Mui-disabled': { color: FORTA_BLUE.primary },
          }
        : {
            '&:focus-within': {
              border: `1px solid ${border}`,
            },
          }),
    };
  };
}

export function mergeRadioGroupBorderSx(
  borderSx: SxProps<Theme>,
  userSx: SxProps<Theme> | undefined,
): SxProps<Theme> {
  if (userSx == null) {
    return borderSx;
  }
  return [borderSx, userSx];
}

export function buildInputLabelSx(inputLabelUserSx: SxProps<Theme> | undefined): SxProps<Theme> {
  return [
    (theme) => ({
      position: 'absolute',
      top: 0,
      left: '14px',
      zIndex: 1,
      transform: `translate(0, ${INPUT_LABEL_NUDGE}px) scale(0.75)`,
      transformOrigin: 'top left',
      backgroundColor: theme.palette.background.paper,
      maxWidth: 'calc(100% - 24px)',
      px: 0.5,
      pointerEvents: 'none',
      lineHeight: 1.4375,
      color: FORTA_BLACK,
      '&.Mui-disabled': { color: FORTA_BLACK },
    }),
    ...(inputLabelUserSx != null
      ? Array.isArray(inputLabelUserSx)
        ? inputLabelUserSx
        : [inputLabelUserSx]
      : []),
  ];
}
